import {
  AGS_COOKIE_DOMAIN,
  COOKIE_DOMAIN,
  SPECTRABASE_COOKIE_DOMAIN,
} from '@src/global/App/constants/constants';
import {
  buildHeaderFooterUrl,
  injectInlineStyles,
  getInlineStyleContents,
} from './utils';
export interface HeaderFooter {
  header: string;
  footer: string;
  // callers accept either NodeList or arrays; use a union so both are supported
  scripts?: NodeListOf<HTMLScriptElement> | HTMLScriptElement[] | [];
  // styles are <link rel="stylesheet"> elements
  styles: NodeListOf<HTMLLinkElement> | HTMLLinkElement[] | string[] | [];
  source?: string;
}

const validateDomain = () => {
  const domain = window.location.hostname;
  const allowedDomains = [
    COOKIE_DOMAIN,
    AGS_COOKIE_DOMAIN,
    SPECTRABASE_COOKIE_DOMAIN,
  ];
  const isDomainAllowed = allowedDomains.some((allowedDomain) =>
    domain.includes(allowedDomain)
  );

  if (!isDomainAllowed) {
    throw new Error(
      `Unknown domain: ${domain}; allowed domains: ${COOKIE_DOMAIN}, ${AGS_COOKIE_DOMAIN}, ${SPECTRABASE_COOKIE_DOMAIN}`
    );
  }
};

export const getHeaderFooter = async (): Promise<HeaderFooter> => {
  try {
    validateDomain();
    const responseData = await buildHeaderFooterUrl();
    // If buildHeaderFooterUrl returned an object with separate header/footer urls,
    // fetch them individually and return their body HTML and aggregated assets.
    // eslint-disable-next-line max-len
    if (
      responseData &&
      typeof responseData !== 'string' &&
      'headerUrl' in responseData &&
      'footerUrl' in responseData
    ) {
      try {
        const [headerRes, footerRes] = await Promise.all([
          fetch(responseData.headerUrl),
          fetch(responseData.footerUrl),
        ]);
        if (!headerRes.ok || !footerRes.ok) {
          throw new Error('spectrabase Header or Footer not found');
        }
        const [headerText, footerText] = await Promise.all([
          headerRes.text(),
          footerRes.text(),
        ]);

        const parser = new DOMParser();
        const headerDoc = parser.parseFromString(headerText, 'text/html');
        const footerDoc = parser.parseFromString(footerText, 'text/html');

        // Use the full body HTML of each document; older selector ids may not exist
        const headerHtml = headerDoc.body ? headerDoc.body.innerHTML : '';
        const footerHtml = footerDoc.body ? footerDoc.body.innerHTML : '';

        const footerStyles = footerDoc.querySelectorAll(
          'link[rel="stylesheet"]'
        );
        const headerStyles = headerDoc.querySelectorAll(
          'link[rel="stylesheet"]'
        );

        const stylesLinks: string[] = [];

        if (headerStyles && headerStyles.length > 0) {
          headerStyles.forEach((style: any) => {
            const styleSrc = style.getAttribute('href') || '';
            if (!stylesLinks.includes(styleSrc)) {
              stylesLinks.push(styleSrc);
            }
          });
        }

        if (footerStyles && footerStyles.length > 0) {
          footerStyles.forEach((style: any) => {
            const styleSrc = style.getAttribute('href') || '';
            if (!stylesLinks.includes(styleSrc)) {
              stylesLinks.push(styleSrc);
            }
          });
        }

        if ('headerFooterCSS' in responseData) {
          const cssHref = responseData.headerFooterCSS as string;
          if (cssHref !== '' && !stylesLinks.includes(cssHref)) {
            stylesLinks.push(cssHref);
          }
        }

        injectInlineStyles([
          ...getInlineStyleContents(headerDoc),
          ...getInlineStyleContents(footerDoc),
        ]);

        console.log('Header Footer CSS Links:', stylesLinks);
        return {
          header: headerHtml,
          footer: footerHtml,
          styles: stylesLinks,
          source: responseData.source ?? '',
        };
      } catch (spectraError) {
        // Handle Spectrabase-specific fetch/parsing errors
        // eslint-disable-next-line no-console
        console.error('Spectrabase header/footer fetch error:', spectraError);
        return {
          header: '',
          footer: '',
          scripts: [],
          styles: [],
          source: '',
        };
      }
    }

    // Legacy combined behaviour: fetch a single document and extract header/footer by id
    const response: Response = await fetch(responseData as string);
    const text: string = await response.text();
    const parser = new DOMParser();
    const data: Document = parser.parseFromString(text, 'text/html');
    const headerData: Element | null = data.querySelector(
      'div#wiley-shared-global-header'
    );
    const footerData: Element | null = data.querySelector(
      'div#wiley-shared-global-footer'
    );
    // scripts from 2.0 header footer have type application/json and not suppose to move from its place
    const scripts: NodeListOf<HTMLScriptElement> = data.querySelectorAll(
      'script:not(#wiley-shared-global-header script):not(#wiley-shared-global-footer script)'
    );
    const styles: NodeListOf<HTMLLinkElement> = data.querySelectorAll(
      'link[rel="stylesheet"]'
    );
    if (!headerData || !footerData) {
      throw new Error('Header or Footer not found');
    }

    return {
      header: headerData.outerHTML,
      footer: footerData.outerHTML,
      scripts,
      styles,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return {
      header: '',
      footer: '',
      scripts: [],
      styles: [],
    };
  }
};
